import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'destructive' | 'accent' | 'success' | 'warning' | 'error' | 'info' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  dot?: boolean;
  status?: string;
  priority?: string;
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { 
      className, 
      variant = 'default', 
      size = 'md',
      dot = false,
      status,
      priority,
      children, 
      ...props 
    },
    ref
  ) => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'font-semibold',
      'rounded-full',
      'uppercase',
      'tracking-wide',
      'transition-colors',
      'duration-150',
      'ease-out',
    ];

    const sizeClasses = {
      xs: ['px-1.5', 'py-0.5', 'text-[10px]', 'gap-1'],
      sm: ['px-2', 'py-0.5', 'text-xs', 'gap-1'],
      md: ['px-2.5', 'py-1', 'text-xs', 'gap-1.5'],
      lg: ['px-3', 'py-1.5', 'text-sm', 'gap-2'],
    };

    // Variant-based classes using new high-tech theme system
    const variantClasses: Record<string, string[]> = {
      default: [
        'bg-muted/80',
        'text-muted-foreground',
        'border border-border/70',
      ],
      primary: [
        'bg-primary/12',
        'text-primary',
        'border border-primary/25',
      ],
      secondary: [
        'bg-secondary/90',
        'text-secondary-foreground',
        'border border-border/80',
      ],
      destructive: [
        'bg-destructive/12',
        'text-destructive',
        'border border-destructive/25',
      ],
      accent: [
        'bg-accent/80',
        'text-accent-foreground',
        'border border-accent-foreground/20',
      ],
      success: [
        'bg-emerald-500/12',
        'text-emerald-600 dark:text-emerald-400',
        'border border-emerald-500/25',
      ],
      warning: [
        'bg-amber-500/12',
        'text-amber-600 dark:text-amber-400',
        'border border-amber-500/25',
      ],
      error: [
        'bg-rose-500/12',
        'text-rose-600 dark:text-rose-400',
        'border border-rose-500/25',
      ],
      info: [
        'bg-sky-500/12',
        'text-sky-600 dark:text-sky-400',
        'border border-sky-500/25',
      ],
      outline: [
        'bg-transparent',
        'text-foreground',
        'border border-border/80',
      ],
    };

    let activeVariant = variant;
    if (status) {
      if (['ACTIVE', 'COMPLETED', '승인', '출근', '완료'].includes(status)) activeVariant = 'success';
      else if (['PENDING', '대기', '진행중'].includes(status)) activeVariant = 'warning';
      else if (['INACTIVE', 'REJECTED', '반려', '퇴소', '취소'].includes(status)) activeVariant = 'destructive';
      else activeVariant = 'info';
    } else if (priority) {
      if (['HIGH', 'URGENT', '응급', '높음'].includes(priority)) activeVariant = 'destructive';
      else if (['MEDIUM', '보통'].includes(priority)) activeVariant = 'warning';
      else activeVariant = 'info';
    }

    const colorClasses = variantClasses[activeVariant] || variantClasses.default;

    const classes = clsx(
      baseClasses,
      sizeClasses[size],
      colorClasses,
      className
    );

    const dotElement = dot && (
      <span className="w-1.5 h-1.5 bg-current rounded-full flex-shrink-0" />
    );

    return (
      <span
        ref={ref}
        className={classes}
        {...props}
      >
        {dot && dotElement}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };